import gql from 'graphql-tag';
import className from 'classnames/bind';
import { ComparisonTableFragment } from '@graphqlTypes';
import { useRef } from 'react';
import { Button, Container, HeadlineAnim, RevealWrapper, RhLink } from '@components';
import TableXMark from '@assets/svg/table-x-mark.svg';
import TableCheckMark from '@assets/svg/table-check-mark.svg';
import { getHighlightMarkup, getLinkData } from '@/core/utils';

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';

import styles from './ComparisonTable.module.scss';
const cx = className.bind(styles);

type CellProps = ComparisonTableFragment['table']['rows'][number]['rowValues'][number];
type FooterCellProps = ComparisonTableFragment['table']['comparisonTableFooter'][number] & {
  columnHeaders: ComparisonTableFragment['table']['columnHeaders'];
  index: number;
};

const Cell = (props: CellProps) => {
  const { rowType, text } = props;
  const [type] = rowType;
  const isText = type === 'text';
  const isCheckmark = type === 'checkmark';
  const isXMark = type === 'xmark';
  return (
    <>
      {isText && <span alt-label={text}>{text}</span>}
      {isCheckmark && <><TableCheckMark aria-hidden={true} /><span className="sr-only">{"YES"}</span></>}
      {isXMark && <><TableXMark aria-hidden={true} /><span className="sr-only">{"NO"}</span></>}
    </>
  )
}

const FooterCell = (props: FooterCellProps) => {
  const { index, text, cta, links, columnHeaders } = props;
  const ctaData = getLinkData(cta);
  return (
    <td className={cx(["footer-cell"])}>
      <div className={cx(["content", "text-m"])}>
        {text && <p className={cx("text-l", "text")}>{text}</p>}
        { ctaData && (
          <Button
            className={cx("cta")}
            label={ctaData.label}
            ariaLabel={columnHeaders[index]?.text ? `${ctaData.label} as ${columnHeaders[index].text}` : ctaData.label}
            noIcon={true}
            url={ctaData.url}
            target={ctaData.target}
          />
        )}
        {links && (
          <div className={cx("links")}>
            {links.map((link, index) => {
              const linkData = getLinkData(link);
              if (!linkData) return null;
              return (
                <RhLink
                  key={index}
                  className={cx("link")}
                  href={linkData.url}
                  target={linkData.target}
                >
                  {linkData.label}
                </RhLink>
              );
            })}
          </div>
        )}
      </div>
    </td>
  );
};

export default function ComparisonTable({ table, headline, body }: ComparisonTableFragment) {
  const { columnHeaders, rows, footerLabel, comparisonTableFooter } = table;
  const headlineMarkup = getHighlightMarkup(headline);
  const showHeader = Boolean(headlineMarkup || body);

  // -------------------- Animation

  const { isDesktop } = useViewSize();

  const rootEl = useRef<HTMLDivElement>(null);
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  return (
    <section className={cx(["comparison-table"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container>
          {showHeader && (
            <div className={cx("header")}>
              {headline && <HeadlineAnim
                tag="h2"
                text={headlineMarkup}
                className={cx('headline', 'title-uc-l')}
                isRevealEnabled={true}
                isInView={isInView}
              />}
              {body && <p className={cx("body", "text-m")}>{body}</p>}
            </div>
          )}
        </Container>
        <Container className={cx("table-wrapper")}>
          <table
            style={{ '--column-count': columnHeaders.length + 1 } as React.CSSProperties}
          >
            <thead>
              <tr>
                <th />
                {columnHeaders.map((header, index) => (
                  <th className={cx(["header-cell", "text-l"])} key={index}>{header.text}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td className={cx(["row-label", "text-s"])}>{row.rowLabel}</td>
                  {row.rowValues.map((cell, cellIndex) => (
                    <td className={cx(["cell", "text-s"])} key={cellIndex}>
                      <Cell {...cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className={cx(["footer-label", "text-l"])}>{footerLabel}</td>
                { comparisonTableFooter?.map((footerItem, index) => (
                  <FooterCell columnHeaders={columnHeaders} index={index} key={index} {...footerItem} />
                ))}
              </tr>
            </tfoot>
          </table>
        </Container>
     </RevealWrapper>
    </section>
  )
}

ComparisonTable.fragments = {
  entry: gql`
    fragment ComparisonTable on FlexiblePageContentContentComparisonTableLayout {
      headline
      body
      table {
        columnHeaders {
          text
        }
        rows {
          rowLabel
          rowValues {
            rowType
            text
          }
        }
        footerLabel
        comparisonTableFooter {
          text
          cta {
            linkType
            label
            externalLink
            page {
              node {
                uri
              }
            }
          }
          links {
            linkType
            externalLink
            label
            page {
              node {
                uri
              }
            }
          }
        }
      }
    }
  `,
};